import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@eombor/ui';

export interface SuggestOption {
  /** Ro'yxat kaliti — tanlanganda chaqiruvchiga shu qaytadi. */
  id: string;
  /** Asosiy matn (kod) — moslik shu bo'yicha ko'zga tashlanadi. */
  label: string;
  /** Tushuntiruvchi matn: nom, o'lchov birligi, manzil. Joy yetmasa kesiladi. */
  hint?: string;
  /**
   * Ogohlantiruvchi belgi (masalan "bloklangan"). `hint` dan AYRI, chunki u
   * hech qachon kesilmasligi kerak: kesilgan ogohlantirish — yo'q ogohlantirish.
   */
  tag?: string;
  /**
   * Ijobiy belgi — "bu qator aynan siz izlagan narsa" (masalan katak shu
   * mahsulot uchun ajratilgan). `tag` dan AYRI: u ogohlantirish rangida
   * va ikkovi bitta qatorda birga uchrashi mumkin — ajratilgan, lekin
   * bloklangan katak.
   */
  mark?: string;
}

export interface UseSuggestResult {
  /** Faol qatorning tartib raqami — `-1` bo'lmaydi, ro'yxat bo'sh bo'lsa `0`. */
  active: number;
  setActive: (index: number) => void;
  /** Ro'yxat va `aria` bog'lanishlari uchun barqaror identifikatorlar. */
  listId: string;
  optionId: (index: number) => string;
  /**
   * Tugma bosilishini o'zi hal qiladi. `true` qaytarsa — hodisa YUTILDI va
   * chaqiruvchi o'z mantiqini (masalan `Enter` bilan keyingi maydonga o'tish)
   * ishga tushirmasligi kerak.
   */
  handleKeyDown: (
    event: KeyboardEvent<HTMLElement>,
    handlers: { onPick: (index: number) => void; onEscape?: () => void },
  ) => boolean;
}

/**
 * Ochiladigan taklif ro'yxatining KLAVIATURA mantig'i.
 *
 * Ekran skaner-birinchi: sichqoncha ixtiyoriy, `↑`/`↓`/`Enter`/`Esc` esa shart.
 * Mantiq ko'rinishdan ajratilgan, chunki uni ikki joy ishlatadi —
 * nomenklatura qidiruvi va katak kodi takliflari — va ikkalasida `Enter` ning
 * "ro'yxat ochiq" va "ro'yxat yopiq" holatidagi ma'nosi boshqa-boshqa.
 */
export const useSuggest = (count: number, open: boolean): UseSuggestResult => {
  const listId = useId();
  const [active, setActive] = useState(0);

  const optionId = useCallback(
    (index: number): string => `${listId}-option-${String(index)}`,
    [listId],
  );

  /* Ro'yxat qisqarganda faol qator chegaradan chiqib ketmasin: aks holda
     `Enter` mavjud bo'lmagan qatorni tanlab, jimgina hech nima qilmaydi. */
  useEffect(() => {
    setActive((current) => (current >= count ? 0 : current));
  }, [count]);

  const handleKeyDown: UseSuggestResult['handleKeyDown'] = (event, handlers) => {
    if (!open || count === 0) {
      // Ro'yxat yopiq bo'lsa `Esc` ham yutilmaydi — u yuqoridagi oynaga tegishli.
      return false;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((current) => (current + 1) % count);
      return true;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => (current - 1 + count) % count);
      return true;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      handlers.onPick(active);
      return true;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      handlers.onEscape?.();
      return true;
    }
    return false;
  };

  return { active, setActive, listId, optionId, handleKeyDown };
};

export interface SuggestListProps {
  options: SuggestOption[];
  active: number;
  listId: string;
  optionId: (index: number) => string;
  /** Ro'yxat nomi — ekran o'quvchi uchun, ko'rinmaydi. */
  label: string;
  /** Hech narsa topilmaganda chiqadigan matn: jim bo'sh ro'yxat qoldirilmaydi. */
  emptyText: string;
  /** Ro'yxat SHU elementning ostiga osiladi — odatda maydonning o'ram tuguni. */
  anchorRef: RefObject<HTMLElement | null>;
  /**
   * Eng kichik kenglik, piksel. Maydon tor ustunda tursa ham izoh kesilmasin:
   * 13rem li ustunga "A-01-01-01" bilan birga manzil sig'maydi.
   */
  minWidth?: number;
  onPick: (index: number) => void;
  onHover: (index: number) => void;
  /** Qatorning o'ng chetidagi qo'shimcha amal — masalan katak kartochkasi. */
  action?: SuggestAction;
  className?: string;
}

/**
 * Qatorning O'NG CHETIDAGI amal — tanlashdan boshqa narsa.
 *
 * Qatorning o'zi bosilsa TANLANADI va bu o'zgarmaydi: skaner bilan
 * ishlayotgan odam uchun eng tez yo'l shu. Amal esa alohida nishon —
 * "tanlashdan oldin ko'rib olay" degan boshqa niyat.
 */
export interface SuggestAction {
  icon: ReactNode;
  /** Ekran o'quvchi uchun nom — qatorning matni bilan to'ldiriladi. */
  label: (option: SuggestOption) => string;
  onAct: (index: number) => void;
}

/** Suzuvchi ro'yxat va maydon orasidagi tirqish, piksel. */
const GAP = 4;

/** Ekran chetigacha qoldiriladigan bo'shliq, piksel. */
const MARGIN = 8;

/** Ro'yxatning eng katta balandligi, piksel — undan uzunini odam o'qimaydi. */
const MAX_HEIGHT = 256;

/** Pastda shundan kam joy qolsa ro'yxat maydonning USTIGA ochiladi. */
const MIN_HEIGHT = 120;

interface Box {
  left: number;
  width: number;
  maxHeight: number;
  /** Ikkovidan BITTASI beriladi: ro'yxat pastga yoki tepaga ochiladi. */
  top?: number;
  bottom?: number;
}

/**
 * Ro'yxatning ekrandagi o'rni.
 *
 * `fixed` va sahifa tanasiga portal — `absolute` YETMAYDI: maydon
 * `overflow-hidden` bo'lgan `Card` ichida turadi va suzuvchi ro'yxat
 * kartochka chegarasida kesilib qolardi. Portal esa hech qanday
 * `overflow` ga uchramaydi.
 *
 * Siljish `capture` bilan tinglanadi: sahifaning o'zi emas, ICHKI
 * qutilar ham siljiydi va ular `window` ga hodisa yubormaydi — ro'yxat
 * esa maydondan ajralib, ekranning o'rtasida osilib qolmasligi kerak.
 */
const useAnchoredBox = (
  anchorRef: RefObject<HTMLElement | null>,
  minWidth: number,
  deps: number,
): Box | null => {
  const [box, setBox] = useState<Box | null>(null);

  useLayoutEffect(() => {
    const place = (): void => {
      const anchor = anchorRef.current;
      if (anchor === null) return;

      const rect = anchor.getBoundingClientRect();
      const width = Math.min(
        Math.max(rect.width, minWidth),
        globalThis.innerWidth - MARGIN * 2,
      );
      // Ekran chetidan chiqib ketmasin: tor ustundagi keng ro'yxat
      // o'ng tomondan tashqariga chiqib, izohi ko'rinmay qolardi.
      const left = Math.max(
        MARGIN,
        Math.min(rect.left, globalThis.innerWidth - width - MARGIN),
      );

      const below = globalThis.innerHeight - rect.bottom - MARGIN;
      const above = rect.top - MARGIN;
      const upward = below < MIN_HEIGHT && above > below;

      setBox({
        left,
        width,
        maxHeight: Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, upward ? above : below)),
        ...(upward
          ? { bottom: globalThis.innerHeight - rect.top + GAP }
          : { top: rect.bottom + GAP }),
      });
    };

    place();
    globalThis.addEventListener('resize', place);
    globalThis.addEventListener('scroll', place, true);
    return () => {
      globalThis.removeEventListener('resize', place);
      globalThis.removeEventListener('scroll', place, true);
    };
  }, [anchorRef, minWidth, deps]);

  return box;
};

/**
 * Maydon ostidagi taklif ro'yxati.
 *
 * SUZADI va sahifa tanasiga PORTAL qilinadi. Ilgari u oqim ichida
 * turardi va ochilganda tagidagi hamma narsani pastga surib yuborardi —
 * kirim sahifasida bu butun satrlar jadvalini sakratardi va odam
 * yozayotgan joyi ekranning boshqa yerida qolib ketardi. Oddiy
 * `absolute` yetmaydi: maydon `overflow-hidden` bo'lgan `Card` ichida
 * va ro'yxat kartochka chegarasida kesilardi.
 *
 * `onMouseDown` da `preventDefault`: sichqoncha bilan bosilganda maydon
 * fokusni yo'qotmasin, aks holda ro'yxat bosilishidan OLDIN yopiladi.
 */
export const SuggestList = ({
  options,
  active,
  listId,
  optionId,
  label,
  emptyText,
  anchorRef,
  minWidth = 0,
  onPick,
  onHover,
  action,
  className,
}: SuggestListProps) => {
  const listRef = useRef<HTMLUListElement | null>(null);
  const box = useAnchoredBox(anchorRef, minWidth, options.length);

  /* Faol qator doim ko'rinib tursin — uzun ro'yxatda `↓` bilan yurganda
     tanlov ekrandan chiqib ketmasligi kerak. */
  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active, options.length]);

  const shell = cn(
    'rounded-[var(--radius-sm)] border border-border bg-surface',
    'shadow-[var(--shadow-2)]',
    className,
  );

  /* O'rni hali o'lchanmagan — chizilmaydi. Bir kadr uchun bo'lsa ham
     ekranning chap yuqori burchagida paydo bo'lib, keyin sakrab
     joyiga o'tishi ko'zga tashlanardi. */
  if (box === null) return null;

  const frame = (
    <div
      className="motion-fade-in fixed z-[var(--z-popover)]"
      style={{
        left: box.left,
        width: box.width,
        ...(box.top === undefined ? {} : { top: box.top }),
        ...(box.bottom === undefined ? {} : { bottom: box.bottom }),
      }}
    >
      {options.length === 0 ? (
        /* `id` bo'sh holatda ham qo'yiladi: maydondagi `aria-controls` hech
           qachon mavjud bo'lmagan tugunga ko'rsatmasligi kerak. */
        <div id={listId} className={shell}>
          <p role="status" className="px-3 py-2 text-sm text-text-muted">
            {emptyText}
          </p>
        </div>
      ) : (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={label}
          style={{ maxHeight: box.maxHeight }}
          className={cn(shell, 'overflow-y-auto py-1')}
        >
          {options.map((option, index) => {
            const selected = index === active;
            return (
              <li
                key={option.id}
                id={optionId(index)}
                role="option"
                aria-selected={selected}
                onMouseMove={() => onHover(index)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => onPick(index)}
                className={cn(
                  'flex cursor-pointer items-baseline gap-2 px-3 py-1.5 text-sm',
                  'transition-[background-color] duration-[var(--motion-fast)]',
                  'ease-[var(--ease-standard)]',
                  selected ? 'bg-accent-soft text-text' : 'text-text-muted',
                )}
              >
                <span className="tabular shrink-0 font-medium text-text">{option.label}</span>
                {option.mark === undefined ? null : (
                  <span
                    className={cn(
                      'bg-accent-soft text-accent border-accent/25 shrink-0 self-center',
                      'rounded-[var(--radius-pill)] border px-1.5 py-px text-[0.625rem]',
                    )}
                  >
                    {option.mark}
                  </span>
                )}
                {option.hint === undefined ? null : (
                  <span className="min-w-0 truncate text-xs text-text-faint">{option.hint}</span>
                )}
                {/* Belgi va amal BITTA o'ng guruhda: ikkovi ham alohida
                    `ml-auto` olsa, birinchisi ikkinchisini chetga qisib
                    qo'yardi va ular orasida bo'shliq qolmasdi. */}
                <span className="ml-auto flex shrink-0 items-center gap-2 self-center">
                  {option.tag === undefined ? null : (
                    <span className="text-xs text-warn">{option.tag}</span>
                  )}
                  {action === undefined ? null : (
                    <button
                      type="button"
                      aria-label={action.label(option)}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={(event) => {
                        // Qatorning o'zi TANLAYDI — amal uni bosib ketmasin.
                        event.stopPropagation();
                        action.onAct(index);
                      }}
                      className={cn(
                        'flex size-6 items-center justify-center rounded-[var(--radius-sm)]',
                        'text-text-faint transition-colors duration-[var(--motion-fast)]',
                        'hover:bg-surface-2 hover:text-text',
                      )}
                    >
                      {action.icon}
                    </button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  return createPortal(frame, document.body);
};
